import React, { FC, useState } from "react";
import { DateTime } from "luxon";
import DayPicker from "react-day-picker";
import "react-day-picker/lib/style.css";
import { t } from "@helpers/translate";
import { Calendar } from "@graphql/types";
import { useLocales } from "@helpers/hooks/useLocales";
import { format } from "date-fns";
import { InputModal } from "@components/ui/Form";

interface InputProps {
  calendar?: Calendar;
  className?: string;
  defaultValue?: Date | undefined;
  showErrorView?: boolean;
  onApply: (value: Date) => void;
  showModal?: boolean;
}

export const InputDate: FC<InputProps> = ({
  defaultValue,
  onApply,
  calendar,
  showErrorView = false,
  className = "",
  showModal = false,
}) => {
  const { locale } = useLocales();
  const [value, setValue] = useState(defaultValue);

  const [isOpen, setIsOpen] = useState(false);

  const initialMonth = value || DateTime.now().toJSDate();

  return (
    <InputModal
      icon="calendar"
      showErrorView={showErrorView}
      className={className}
      title={t({ id: "tour.input.date", message: "Date" })}
      value={
        defaultValue
          ? DateTime.fromJSDate(defaultValue).toFormat("DD", { locale })
          : undefined
      }
      onApply={() => value && onApply(value)}
      setIsGlobalOpen={(open: boolean) => setIsOpen(open)}
      checkIsOpen={() => {
        if (showModal) {
          return true;
        }
        return isOpen;
      }}
      showApply={false}
    >
      <DayPicker
        initialMonth={initialMonth}
        firstDayOfWeek={0}
        selectedDays={value ? [value] : []}
        disabledDays={[
          (date) => {
            if (!calendar) return false;

            const isNotWorkingDate = calendar.notWorkingDates.find(
              (d) => d === format(date, "yyyy-MM-dd")
            );
            const isWorkingDate = calendar.workingDates.find(
              (d) => d === format(date, "yyyy-MM-dd")
            );

            if (
              calendar.minDate &&
              format(date, "yyyy-MM-dd") < calendar.minDate &&
              !isWorkingDate
            ) {
              return true;
            }

            if (
              calendar.maxDate &&
              format(date, "yyyy-MM-dd") > calendar.maxDate &&
              !isWorkingDate
            ) {
              return true;
            }

            if (isWorkingDate) {
              return false;
            }

            if (!calendar.daysOfWeekAvailable.includes(date.getDay())) {
              return true;
            }

            if (isNotWorkingDate) {
              return true;
            }

            return false;
          },
          {
            before: DateTime.now().toJSDate(),
          },
        ]}
        onDayClick={(day, modifiers) => {
          if (!modifiers.disabled) {
            setValue(day);
            onApply(day);
            setIsOpen(false);
          }
        }}
        className="w-full"
      />
    </InputModal>
  );
};
